import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ButtonGroup, Button } from '@appcues/sonar';
import {
  Heading,
  InputClickToCopy,
  Input,
  Modal,
  ModalHeader,
} from 'ext/components/ui';
import { useAnalytics } from '@appcues/analytics/hooks/use-analytics';
import { update as updateName } from 'entities/experiences/actions';
import { update as updateTheme } from 'entities/compiled-theme/actions';
import { selectThemes } from 'entities/themes/reducer';
import ThemesShape from 'entities/themes/shape';
import { Form, FormGroup, Label } from 'components/Form';
import ThemeSelect from './ThemeSelect';

export const FlowDetails = ({
  flow,
  themes = {},
  onClose,
  onSaveName,
  onSaveTheme,
  visible = true,
}) => {
  const { mobileThemes } = useFlags();
  const { track } = useAnalytics();

  const [name, setName] = useState('');
  const [theme, setTheme] = useState();

  useEffect(() => {
    setName(flow.name || '');
  }, [flow, setName]);

  useEffect(() => {
    setTheme(flow.theme);
  }, [flow, setTheme]);

  const handleNameChange = useCallback(
    ({ target: { value } }) => {
      setName(value);
    },
    [setName]
  );

  const handleThemeChange = selected => {
    setTheme(selected?.value);
  };

  const trimmedName = name.trim();
  const disabled =
    trimmedName.length === 0 ||
    (trimmedName === flow.name && theme === flow.theme);

  const handleSave = event => {
    event.preventDefault();

    track('Mobile Builder interaction', {
      name: 'Updated Flow Details',
      component: 'FlowDetails',
    });

    if (theme !== flow.theme) {
      onSaveTheme({
        id: flow.id,
        name: trimmedName,
        theme,
      });
    } else {
      onSaveName({
        id: flow.id,
        name: trimmedName,
      });
    }

    onClose();
  };

  const handleCancel = () => {
    setName(flow.name || '');
    setTheme(flow.theme);
    onClose();
  };

  return (
    <Modal onClose={handleCancel} visible={visible} theme="light">
      <ModalHeader>
        <Heading>Edit Flow</Heading>
        <ModalHeader.Close onClose={handleCancel} />
      </ModalHeader>
      <Form disabled={disabled} onSubmit={handleSave} role="form">
        <FormGroup>
          <Label htmlFor="name">Flow name</Label>
          <Input
            id="name"
            onChange={handleNameChange}
            placeholder="Enter flow name..."
            role="textbox"
            value={name}
          />
        </FormGroup>
        {mobileThemes && (
          <FormGroup>
            <Label htmlFor="theme">Theme</Label>
            <ThemeSelect
              selectedThemeId={theme}
              themes={themes}
              onThemeChange={handleThemeChange}
            />
          </FormGroup>
        )}
        <FormGroup>
          <Label>Flow ID</Label>
          <InputClickToCopy text={flow.id} wrap />
        </FormGroup>
        <ButtonGroup right>
          <Button variant="tertiary" onClick={handleCancel} type="button">
            Cancel
          </Button>
          <Button
            disabled={disabled}
            variant="primary"
            onClick={handleSave}
            type="submit"
          >
            Save
          </Button>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

FlowDetails.propTypes = {
  flow: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    theme: PropTypes.string,
  }),
  themes: PropTypes.objectOf(ThemesShape),
  onClose: PropTypes.func,
  onSaveName: PropTypes.func,
  onSaveTheme: PropTypes.func,
  visible: PropTypes.bool,
};

const mapStateToProps = state => ({
  themes: selectThemes(state),
});

const mapDispatchToProps = {
  onSaveName: updateName,
  onSaveTheme: updateTheme,
};

export default connect(mapStateToProps, mapDispatchToProps)(FlowDetails);
